import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./VaultDoorPaywall.module.scss"

/**
 * VaultDoorPaywall — an original, self-contained ONBOARDING paywall.
 *
 * Concept ("Open your vault"): the very first thing a new user sees is not a
 * feature grid or a price table — it's a single object they have to *operate*.
 * A photoreal circular BANK-VAULT DOOR fills the screen, locked. The user grabs
 * the central handwheel and physically TURNS it (drag to spin, or tap to crank).
 * As the wheel rotates, the boltwork retracts, a ring of pins flips red→green,
 * an "UNLOCKING %" readout climbs, and — when it hits 100% — a deep clunk swings
 * the heavy door ajar. The cold steel room floods with warm vault light and the
 * offer is revealed *inside*. The act of opening their own vault is the moonshot:
 * a tactile mechanism, not a passive scene, so the user has already invested a
 * gesture before the ask (endowment + delight = conversion).
 *
 * The whole layout funnels to the YEARLY plan: the annual card is the only large,
 * gold-foil-bordered, tappable surface (selected by default), its annual price is
 * the single biggest number on screen (with a tiny per-month equivalent so the
 * commitment feels small and it stays App Store-compliant — the annual price is
 * the most prominent price for the annual product), a 7-day free trial removes
 * friction, the savings vs monthly is shouted, and monthly is demoted to one
 * quiet line. It never implies the user loses access to anything — it's a pure
 * "step inside" onboarding moment.
 *
 * Fully self-contained: no shared paywall components are reused. Reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data. Reuses the
 * brand fonts (RedHatDisplay + KronaOne for the engraved wordmark/price), the
 * brand primary blue (cold steel) and secondary gold (warm interior + foil), and
 * existing repo feature icons for the "inside your vault" list.
 */

const BOLTS = 8
const PINS = 10
const TARGET_DEG = 540 // a turn and a half of the wheel fully unlocks
const DUST_COUNT = 20

const VaultDoorPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData
  const reduceMotion = useReducedMotion()

  const [phase, setPhase] = useState<"locked" | "open">("locked")
  const [progress, setProgress] = useState(0) // 0..1
  const [showClose, setShowClose] = useState(false)
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  const phaseRef = useRef<"locked" | "open">("locked")
  phaseRef.current = phase

  // --- pricing -------------------------------------------------------------
  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perMonthEquivalent = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  const memoryCount =
    productData?.numPhotos && productData.numPhotos > 0
      ? productData.numPhotos
      : 2174

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  // --- native --------------------------------------------------------------
  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#090b11")
    actions.setFooterInsetColor("#090b11")
    const t = setTimeout(() => setShowClose(true), 2800)
    return () => clearTimeout(t)
  }, [actions])

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined =
      selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // --- the spin mechanism --------------------------------------------------
  const stageRef = useRef<HTMLDivElement>(null)
  const wheelRef = useRef<HTMLDivElement>(null)
  const accumRef = useRef(0)
  const lastAngleRef = useRef<number | null>(null)
  const draggingRef = useRef(false)
  const movedRef = useRef(0)
  const rafRef = useRef<number | undefined>(undefined)

  const applyAccum = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(TARGET_DEG, value))
    accumRef.current = clamped
    const p = clamped / TARGET_DEG
    if (wheelRef.current) {
      wheelRef.current.style.setProperty("--wheel", `${clamped}deg`)
    }
    if (stageRef.current) {
      stageRef.current.style.setProperty("--p", p.toFixed(4))
    }
    setProgress(p)
    if (clamped >= TARGET_DEG && phaseRef.current === "locked") {
      setPhase("open")
    }
  }, [])

  const animateAccumTo = useCallback(
    (target: number, duration = 900) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      const start = accumRef.current
      const t0 = performance.now()
      const step = (now: number) => {
        const k = Math.min(1, (now - t0) / duration)
        const eased = 1 - Math.pow(1 - k, 3)
        applyAccum(start + (target - start) * eased)
        if (k < 1) rafRef.current = requestAnimationFrame(step)
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [applyAccum],
  )

  const angleAt = (clientX: number, clientY: number) => {
    const el = wheelRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    return (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (phaseRef.current === "open") return
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    draggingRef.current = true
    movedRef.current = 0
    lastAngleRef.current = angleAt(e.clientX, e.clientY)
    wheelRef.current?.classList.add(styles.wheelGrabbing)
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || lastAngleRef.current === null) return
    const cur = angleAt(e.clientX, e.clientY)
    let delta = cur - lastAngleRef.current
    if (delta > 180) delta -= 360
    if (delta < -180) delta += 360
    lastAngleRef.current = cur
    movedRef.current += Math.abs(delta)
    applyAccum(accumRef.current + delta)
  }

  const endDrag = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    lastAngleRef.current = null
    wheelRef.current?.classList.remove(styles.wheelGrabbing)
    // Treat a near-stationary press as a "crank" tap so click-only also works.
    if (movedRef.current < 9 && phaseRef.current === "locked") {
      animateAccumTo(accumRef.current + TARGET_DEG * 0.37, 620)
    }
  }

  // Reduced motion: present the vault already open so the offer is never gated.
  // Otherwise, a gentle safety auto-open so the offer is always reachable.
  useEffect(() => {
    if (reduceMotion) {
      applyAccum(TARGET_DEG)
      setPhase("open")
      return
    }
    const t = setTimeout(() => {
      if (phaseRef.current === "locked") animateAccumTo(TARGET_DEG, 1300)
    }, 7000)
    return () => {
      clearTimeout(t)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [reduceMotion, applyAccum, animateAccumTo])

  const litPins = Math.round(progress * PINS)
  const pct = Math.round(progress * 100)
  const isOpen = phase === "open"

  return (
    <div
      className={styles.screen}
      data-open={isOpen ? "1" : "0"}
      data-reduce={reduceMotion ? "1" : "0"}
    >
      <Backdrop reduceMotion={!!reduceMotion} />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 14 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.5, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 1 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 14, paddingBottom: bottomInset + 16 }}
      >
        {/* ---------------- the door ---------------- */}
        <div className={styles.doorArea}>
          <motion.div
            className={styles.intro}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className={styles.eyebrow}>
              <span className={styles.eyebrowDot} />
              PRIVATE · ENCRYPTED · YOURS
            </span>
            <h1 className={styles.title}>
              {isOpen ? (
                <>
                  You're <span className={styles.titleAccent}>in.</span>
                </>
              ) : (
                <>
                  Open your <span className={styles.titleAccent}>vault.</span>
                </>
              )}
            </h1>
            <p className={styles.subtitle}>
              {isOpen
                ? "Everything inside is encrypted and yours alone. Unlock all of it with Premium."
                : "Take the wheel and give it a turn to step inside."}
            </p>
          </motion.div>

          <motion.div
            className={styles.stageWrap}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className={styles.stage} ref={stageRef}>
              <div className={styles.interior} aria-hidden>
                <span className={styles.interiorGlow} />
                <span className={styles.interiorRays} />
                <span className={styles.interiorLabel}>
                  {memoryCount.toLocaleString()} memories waiting
                </span>
              </div>

              <div className={styles.hinge} aria-hidden>
                <span />
                <span />
                <span />
              </div>

              <div className={styles.leaf}>
                <div className={styles.edge} aria-hidden />
                <div className={styles.rim} aria-hidden />

                {/* retracting boltwork */}
                <div className={styles.bolts} aria-hidden>
                  {Array.from({ length: BOLTS }, (_, i) => (
                    <span
                      key={i}
                      className={styles.bolt}
                      style={{ "--i": i } as React.CSSProperties}
                    />
                  ))}
                </div>

                <div className={styles.face} aria-hidden>
                  <span className={styles.grooveA} />
                  <span className={styles.grooveB} />
                  <span className={styles.brush} />
                </div>

                <span className={styles.wordmark}>VAULT</span>
                <span className={styles.serial}>NO. 01 · 2026</span>

                {/* status pin ring red -> green */}
                <div className={styles.pinRing} aria-hidden>
                  {Array.from({ length: PINS }, (_, i) => (
                    <span
                      key={i}
                      className={`${styles.pin} ${
                        i < litPins ? styles.pinOn : ""
                      }`}
                      style={{ "--i": i } as React.CSSProperties}
                    />
                  ))}
                </div>

                {/* the handwheel the user turns */}
                <div
                  className={styles.wheel}
                  ref={wheelRef}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  onPointerLeave={endDrag}
                  role="slider"
                  aria-label="Turn the wheel to open your vault"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                >
                  <span className={styles.wheelRing} />
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={styles.spoke}
                      style={
                        {
                          transform: `rotate(${i * 72}deg)`,
                        } as React.CSSProperties
                      }
                    />
                  ))}
                  <span className={styles.hub}>
                    <LockGlyph open={isOpen} />
                  </span>
                  {!isOpen && <span className={styles.turnArrow} aria-hidden />}
                </div>
              </div>
            </div>

            {/* turn hint / progress readout */}
            <AnimatePresence>
              {!isOpen && (
                <motion.div
                  className={styles.readout}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <div className={styles.meterTrack}>
                    <span
                      className={styles.meterFill}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className={styles.readoutLabel}>
                    {pct === 0
                      ? "TURN TO OPEN"
                      : pct >= 100
                        ? "UNLOCKED"
                        : `UNLOCKING ${pct}%`}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* ---------------- the offer (inside) ---------------- */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={styles.offer}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.35,
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <PerkList />

              <div className={styles.plans}>
                <button
                  className={`${styles.yearCard} ${
                    selected === "yearly" ? styles.yearCardActive : ""
                  }`}
                  onClick={() => setSelected("yearly")}
                  aria-pressed={selected === "yearly"}
                >
                  <span className={styles.foilEdge} aria-hidden />
                  <span className={styles.bestRibbon}>SAVE {savings}%</span>

                  <div className={styles.yearHead}>
                    <span
                      className={`${styles.radio} ${
                        selected === "yearly" ? styles.radioOn : ""
                      }`}
                      aria-hidden
                    >
                      {selected === "yearly" && <Check />}
                    </span>
                    <div className={styles.yearHeadText}>
                      <span className={styles.yearName}>
                        Full Access · Yearly
                      </span>
                      <span className={styles.yearSub}>Your best value</span>
                    </div>
                  </div>

                  <div className={styles.priceBlock}>
                    <div className={styles.priceRow}>
                      <span className={styles.priceMain}>{yearlyPrice}</span>
                      <span className={styles.pricePer}>/year</span>
                    </div>
                    <div className={styles.priceSub}>
                      7 days free, then just {perMonthEquivalent}/mo · billed
                      annually
                    </div>
                  </div>
                </button>

                <button
                  className={`${styles.monthlyLine} ${
                    selected === "monthly" ? styles.monthlyLineOn : ""
                  }`}
                  onClick={() => setSelected("monthly")}
                  aria-pressed={selected === "monthly"}
                >
                  Prefer monthly? {monthlyPrice}/mo — standard rate
                </button>
              </div>

              <div className={styles.ctaWrap}>
                <button className={styles.cta} onClick={handlePurchase}>
                  <span className={styles.ctaShimmer} aria-hidden />
                  <span className={styles.ctaLabel}>
                    {selected === "yearly"
                      ? "Start 7 days free"
                      : "Continue monthly"}
                  </span>
                  <span className={styles.ctaArrow}>→</span>
                </button>
                <p className={styles.ctaNote}>
                  No charge today · Cancel anytime in one tap
                </p>
              </div>

              <TrustRow />

              <div className={styles.legal}>
                <button onClick={() => native.postNativeEvent("terms")}>
                  Terms
                </button>
                <span>·</span>
                <button onClick={() => native.postNativeEvent("privacy")}>
                  Privacy
                </button>
                <span>·</span>
                <button
                  onClick={() => native.postNativeEvent("already_purchased")}
                >
                  Restore
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Backdrop: a dark steel vault room — a cold blue spotlight that warms */
/* to amber as the door opens, drifting dust, faint scanlines, vignette */
/* ------------------------------------------------------------------ */
const Backdrop = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const dust = useMemo(
    () =>
      Array.from({ length: DUST_COUNT }, (_, i) => {
        const r1 = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
        const r2 = Math.abs(Math.sin(i * 78.233) * 12543.123) % 1
        const r3 = Math.abs(Math.sin(i * 4.123) * 9973.77) % 1
        return {
          left: `${r1 * 100}%`,
          size: 1.5 + r3 * 3,
          delay: `${r2 * 10}s`,
          dur: `${10 + r1 * 10}s`,
          drift: `${(r3 - 0.5) * 46}px`,
        }
      }),
    [],
  )

  return (
    <div className={styles.scene} aria-hidden>
      <div className={styles.spotCold} />
      <div className={styles.spotWarm} />
      <div className={styles.scan} />
      {!reduceMotion && (
        <div className={styles.dust}>
          {dust.map((d, i) => (
            <span
              key={i}
              className={styles.mote}
              style={
                {
                  left: d.left,
                  width: d.size,
                  height: d.size,
                  animationDelay: d.delay,
                  animationDuration: d.dur,
                  "--drift": d.drift,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
      <div className={styles.vignette} />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* What's inside — perk list using existing repo feature icons.         */
/* ------------------------------------------------------------------ */
const PERKS = [
  {
    Icon: svg.featureIcons.CloudVault,
    text: "Unlimited encrypted cloud vault",
  },
  {
    Icon: svg.featureIcons.Breakin,
    text: "Break-in alerts & intruder capture",
  },
  { Icon: svg.featureIcons.Decoy, text: "Decoy vault & app disguise" },
  { Icon: svg.featureIcons.Star, text: "Ad-free, members-only experience" },
]

const PerkList = () => (
  <div className={styles.perks}>
    <span className={styles.perksTitle}>Inside your vault</span>
    <div className={styles.perkRows}>
      {PERKS.map((p, i) => (
        <motion.div
          key={i}
          className={styles.perk}
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
        >
          <span className={styles.perkIcon}>
            <p.Icon />
          </span>
          <span className={styles.perkText}>{p.text}</span>
          <span className={styles.perkCheck}>
            <Check />
          </span>
        </motion.div>
      ))}
    </div>
  </div>
)

const TrustRow = () => (
  <div className={styles.trust}>
    <div className={styles.trustItem}>
      <span className={styles.trustStrong}>4.8★</span>
      <span className={styles.trustLabel}>App Store</span>
    </div>
    <span className={styles.trustSep} />
    <div className={styles.trustItem}>
      <span className={styles.trustStrong}>10M+</span>
      <span className={styles.trustLabel}>vaults</span>
    </div>
    <span className={styles.trustSep} />
    <div className={styles.trustItem}>
      <span className={styles.trustStrong}>Zero-knowledge</span>
      <span className={styles.trustLabel}>only you hold the key</span>
    </div>
  </div>
)

/* ---- inline glyphs ---- */
const LockGlyph = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <rect
      x="5"
      y="10.5"
      width="14"
      height="10"
      rx="2.4"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d={open ? "M8 10.5V8a4 4 0 0 1 7.6-1.7" : "M8 10.5V8a4 4 0 0 1 8 0v2.5"}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <circle cx="12" cy="15.2" r="1.5" fill="currentColor" />
  </svg>
)

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default VaultDoorPaywall
